"use client";

import React, { useState } from "react";
import { MailingListService } from "@/app/service";

interface Props {
  layout?: "inline" | "stacked";
  buttonLabel?: string;
  onSuccess?: (email: string) => void;
  className?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

export default function MailingListSignupForm({
  layout = "stacked",
  buttonLabel = "Subscribe",
  onSuccess,
  className = "",
}: Props) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Email is required");
      return;
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    const res = await MailingListService.subscribe({ email: trimmedEmail, name: name.trim() || undefined });
    setLoading(false);

    if (!res.success) {
      const fieldErr = res.errors?.find((e) => e.field === "email")?.message;
      setError(fieldErr || res.message || "Subscription failed");
      return;
    }

    setSuccess("Thanks! You're subscribed.");
    onSuccess?.(trimmedEmail);
    setEmail("");
    setName("");
  };

  const inputBase = "w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-widertalk-lime transition-colors bg-white text-gray-900 placeholder-gray-400";

  return (
    <form onSubmit={submit} className={`space-y-3 ${className}`}>
      {layout === "inline" ? (
        <div className="flex gap-2 w-full">
          <input
            type="email"
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={`${inputBase} flex-1`}
            aria-label="Email address"
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-widertalk-lime text-widertalk-navy font-medium rounded-lg hover:bg-widertalk-lime/90 disabled:opacity-60 whitespace-nowrap"
          >
            {loading ? "Subscribing..." : buttonLabel}
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputBase}
              aria-label="Name"
              disabled={loading}
            />
            <input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputBase}
              aria-label="Email address"
              disabled={loading}
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2 bg-widertalk-lime text-widertalk-navy font-medium rounded-lg hover:bg-widertalk-lime/90 disabled:opacity-60"
          >
            {loading ? "Subscribing..." : buttonLabel}
          </button>
        </>
      )}

      {error && <p className="text-red-400 text-sm">{error}</p>}
      {success && <p className="text-green-400 text-sm">{success}</p>}
      <p className="text-xs text-gray-400">We respect your privacy. Unsubscribe anytime.</p>
    </form>
  );
}
